/**
 * Client telemetry: a flat, timestamped event stream per session.
 *
 * The PWA reports one event per pipeline stage -- join, score, upload, stop,
 * finalise -- so a session's full timeline is reconstructable from the server
 * logs. This module buffers events and flushes them in batches to
 * `POST /v1/telemetry`, which the server validates and emits as structured logs.
 *
 * The privacy boundary is the same one the rest of the app honours: events carry
 * identifiers and counts, never transcript text, summaries or audio. `detail`
 * values are scalars only, and a string one is short.
 */

import { postClockSync, postTelemetry } from "./api.js";
import {
  CLOCK_SYNC_SCHEMA_VERSION,
  serializeClockSyncMeasurement,
  validateClockSyncProbeRequest,
  validateClockSyncMeasurement,
} from "./clock-sync.js";

let sessionId = null;
const events = [];
let flushPromise = null;
//: Flush before the server's per-batch cap, and well before the buffer could
//: grow large enough that a lost page takes a whole session's timeline with it.
const FLUSH_AT = 20;
const MAX_BATCH = 100;
//: Drain on a clock as well as on a count, because the events worth having are
//: the ones a session produces fewest of.
//:
//: [V] A whole wrap-up went missing this way. Recording emits ~4 events per 20 s
//: chunk, so `FLUSH_AT` is reached every couple of minutes and the mid-session
//: timeline arrives fine. Wrap-up emits perhaps six -- `stop`, the final `score`,
//: the last uploads, `result_ready` -- and then the session is over and nothing
//: else is ever logged. Six is not twenty, so they sat in this buffer. The last
//: flush of that session landed at 20:59:47, two seconds before `close`, and the
//: phase everybody actually needed to see was the one phase that never shipped.
const FLUSH_EVERY_MS = 10000;

export function bindTrace(sid) {
  sessionId = sid;
}

export function log(event, detail = {}, durMs = null) {
  events.push({
    event,
    ts: Date.now(),
    dur_ms: durMs,
    detail,
  });
  if (events.length >= FLUSH_AT) flush();
}

/** Report a failed shadow probe without allowing arbitrary error text into telemetry. */
export function reportClockSyncError(error) {
  const message = error instanceof Error ? error.message : String(error);
  const detail = {
    operation: "clock_sync",
    message: message.slice(0, 64) || "clock sync probe failed",
  };
  if (typeof error?.status === "number" && Number.isFinite(error.status)) {
    detail.status = error.status;
  }
  log("error", detail);
  // A probe failure is itself the useful observation; do not leave it behind in
  // the ordinary batch/visibility flush path if the session is about to end.
  flush();
}

/**
 * Measure one client/server round trip without changing any production timing
 * path. ``capture`` carries adjacent monotonic and epoch anchors plus the
 * current logical sample offset; t1/t4 remain the request-boundary wall-clock
 * readings used solely for clock-skew diagnostics.
 */
export async function measureClockSync(capture) {
  const sample = capture ?? (() => {
    const captureAnchorMonotonicMs = performance.now();
    const captureAnchorEpochMs = Date.now();
    return {
      capture_anchor_monotonic_ms: captureAnchorMonotonicMs,
      capture_anchor_epoch_ms: captureAnchorEpochMs,
      sample_offset: 0,
      sample_rate_hz: 16_000,
      uncertainty_ms: 1,
    };
  })();
  const probe = validateClockSyncProbeRequest({
    version: CLOCK_SYNC_SCHEMA_VERSION,
    ...sample,
    t1_client_send_ms: Date.now(),
  });
  const receipt = await postClockSync(probe);
  const measurement = validateClockSyncMeasurement({
    ...receipt,
    t4_client_receive_ms: Date.now(),
  });
  // Serialize before logging so this path cannot enqueue a value that the
  // server would encode as JSON null (NaN/Infinity) if a browser API regresses.
  const safe = JSON.parse(serializeClockSyncMeasurement(measurement));
  log("clock_sync", safe);
  return measurement;
}

//: How many events this page gave up on, and why it is only a counter. A rejected
//: event cannot be reported *as* telemetry -- that emission would take the same
//: path and could be rejected the same way, and a channel that reports its own
//: failures through itself is how a diagnostic becomes the outage. The counter's
//: job is to bound the console warning below: a client whose schema has drifted
//: from the server's rejects *every* event, and an unbounded warn per drop would
//: bury the console it was meant to inform.
let droppedEvents = 0;
const MAX_DROP_WARNINGS = 5;

/**
 * Recover the HTTP status from a `postTelemetry` rejection.
 *
 * `postTelemetry` (`web/lib/api.js`) throws `Error("telemetry <status>")` and does
 * not set `.status` the way `request()` does, so the status is parsed back out of
 * the message. `.status` is preferred when present, which is both what `request()`
 * would give us and what lets this stop being a parse if `postTelemetry` is ever
 * brought in line with it.
 *
 * A network failure has no status at all, and that distinction is the whole point:
 * "the request never landed" and "the server refused this body" need opposite
 * handling, and conflating them is what made one bad event silent-fail forever.
 */
function rejectionStatus(err) {
  if (typeof err?.status === "number" && Number.isFinite(err.status)) return err.status;
  const parsed = /^telemetry (\d{3})$/.exec(String(err?.message ?? ""));
  return parsed ? Number(parsed[1]) : null;
}

/**
 * Post one batch, returning the events that are still worth keeping.
 *
 * A 4xx that is not 429 means the server refused this *body*; re-sending it
 * unchanged cannot make it acceptable. Rather than drop a whole batch for one bad
 * event, the batch is bisected in place so the refusal is isolated in O(log n)
 * posts and every innocent event still lands. Only a single-event refusal is
 * dropped, and it is the event that was actually refused.
 *
 * [V] This is the #264 evidence loss. The old `catch` re-queued unconditionally at
 * the head of the buffer, so one event the schema refused -- `stream_stop` with
 * `reason: "reconnect"`, which the server's Literal did not accept -- blocked every
 * later event from that device until the page reloaded. The reconnect telemetry and
 * the entire wrap-up behind it went together, which is exactly why reconnect
 * behaviour could not be proven from the logs.
 *
 * The bisection happens inside one flush rather than by re-queuing both halves,
 * because the buffer is a flat array with no batch boundaries: re-queued halves
 * would be spliced back into one batch by the next flush and split forever without
 * ever converging.
 */
async function sendBatch(batch) {
  try {
    await postTelemetry({ session_id: sessionId, events: batch });
    return [];
  } catch (err) {
    const status = rejectionStatus(err);
    // No status (network error), 5xx, or 429: the request never got a verdict, or
    // got "come back later". A socket failure is exactly when the final diagnostic
    // must not disappear with the request.
    //
    // Everything else takes the bounded path, deliberately including a 3xx -- an
    // opaque redirect the Worker did not follow would otherwise re-queue to the
    // same redirect on every flush, which is the unbounded block this slice exists
    // to remove, reintroduced on a narrower path. Only re-queue what a later flush
    // could plausibly get a different answer for.
    if (status === null || status >= 500 || status === 429) return batch;

    if (batch.length === 1) {
      droppedEvents += 1;
      if (droppedEvents <= MAX_DROP_WARNINGS) {
        console.warn("telemetry event rejected", status, batch[0]);
      }
      return [];
    }
    const mid = Math.ceil(batch.length / 2);
    const head = await sendBatch(batch.slice(0, mid));
    const tail = await sendBatch(batch.slice(mid));
    return [...head, ...tail];
  }
}

export function flush() {
  if (flushPromise) {
    return flushPromise.then(() => (events.length ? flush() : undefined));
  }
  if (!events.length) return Promise.resolve();
  const batch = events.splice(0, MAX_BATCH);
  // Fire-and-forget: a retryable failure is retried on the next flush, and the
  // server treats the batch as best-effort. `keepalive` (set in `postTelemetry`)
  // lets the final flush of a session survive the page being torn down.
  flushPromise = sendBatch(batch)
    .then((unsent) => {
      // One unshift, in the original order: two would invert the halves and
      // reorder a timeline whose whole value is its order.
      if (unsent.length) events.unshift(...unsent);
    })
    .finally(() => {
      flushPromise = null;
    });
  return flushPromise;
}

// The last events of a session are emitted around the stop button, and a tab
// closed right after is the one case where they would otherwise never leave.
window.addEventListener("pagehide", () => flush());

// `pagehide` is not enough on its own, and the gap is exactly where the useful
// events live. It does not fire when a phone is merely switched away from -- and
// on iOS that is the common case, not the edge one: the tab stays alive, the page
// is never torn down, and a buffer holding a whole wrap-up simply waits for a
// twentieth event that no longer has any way to arrive. `visibilitychange` covers
// the switch away; the interval covers a page that stays in front the whole time
// and still has fewer than `FLUSH_AT` events to its name.
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") flush();
});

// Unconditional and never cleared: telemetry outlives any one session, and a
// timer that had to be started and stopped somewhere is a timer that is off at
// the moment somebody needs it on. An empty buffer makes `flush` a no-op, so the
// idle cost is one comparison every ten seconds.
setInterval(flush, FLUSH_EVERY_MS);
